import React, { useState, useEffect } from 'react';
import { Link, useParams, useOutletContext } from 'react-router-dom';
import axios from '../../axiosConfig';
import './CollectionsPage.css';

const CollectionsPage = () => {
  const { slug } = useParams();
  const { store } = useOutletContext() || {};
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const storeSlug = slug || store?.slug;

  useEffect(() => {
    const fetchCollections = async () => {
      if (!storeSlug) return;

      setLoading(true);
      try {
        const response = await axios.get(`/collections/store/${storeSlug}`);
        const data = response.data;

        if (data.success && data.data) {
          // Backend returns { success, data: { store, collections } }
          setCollections(data.data.collections || []);
        } else {
          setError(data.error || 'Failed to load collections');
        }
      } catch (err) {
        console.error('Error fetching collections:', err);
        setError('Failed to load collections');
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [storeSlug]);

  if (loading) {
    return (
      <div className="storefront-collections-page">
        <div className="collections-loading">
          <div className="loading-spinner"></div>
          <p>Loading collections...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="storefront-collections-page">
        <div className="collections-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const getCollectionUrl = (collection) => {
    if (slug) {
      return `/s/${slug}/collection/${collection.slug}`;
    }
    return `/collection/${collection.slug}`;
  };

  // Get collection image (cover image or fallback)
  const getCollectionImage = (collection) => {
    return collection.coverImage || collection.image || null;
  };

  return (
    <div className="storefront-collections-page">
      <div className="collections-header">
        <div className="collections-header-text">
          <h1>Collections</h1>
          <p>Browse our curated product collections</p>
        </div>
        {collections.length > 0 && (
          <div className="storefront-view-toggle">
            <button
              className={`sf-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </button>
            <button
              className={`sf-view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {collections.length === 0 ? (
        <div className="collections-empty">
          <svg width="64" height="64" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3>No collections yet</h3>
          <p>Check back soon for curated product collections.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="collections-grid">
          {collections.map((collection) => {
            const collectionImage = getCollectionImage(collection);
            return (
              <Link
                key={collection.id}
                to={getCollectionUrl(collection)}
                className="collection-card"
              >
                <div className="collection-card-image">
                  {collectionImage ? (
                    <img src={collectionImage} alt={collection.name} />
                  ) : (
                    <div className="collection-card-placeholder">
                      <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                  {collection.featured && (
                    <span className="collection-featured-badge">Featured</span>
                  )}
                </div>
                <div className="collection-card-content">
                  <h3 className="collection-card-title">{collection.name}</h3>
                  {collection.description && (
                    <p className="collection-card-description">{collection.description}</p>
                  )}
                  <span className="collection-product-count">
                    {collection._count?.products || 0} products
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="sf-collections-list">
          {collections.map((collection) => {
            const collectionImage = getCollectionImage(collection);
            return (
              <Link
                key={collection.id}
                to={getCollectionUrl(collection)}
                className="sf-collection-list-item"
              >
                <div className="sf-list-image">
                  {collectionImage ? (
                    <img src={collectionImage} alt={collection.name} />
                  ) : (
                    <div className="sf-list-image-placeholder">
                      <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="sf-list-content">
                  <div className="sf-list-main">
                    <h3 className="sf-list-title">{collection.name}</h3>
                    {collection.description && (
                      <p className="sf-list-description">{collection.description}</p>
                    )}
                  </div>
                  <div className="sf-list-meta">
                    {collection.featured && (
                      <span className="collection-featured-badge">Featured</span>
                    )}
                    <span className="sf-list-count">
                      {collection._count?.products || 0} products
                    </span>
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="sf-list-arrow">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CollectionsPage;
